import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TablePagination,
  Stack, MenuItem, Select, FormControl, InputLabel,
  InputAdornment, Chip, IconButton, Tooltip, Skeleton,
  Tabs, Tab, Divider, Menu, useTheme, alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  ContentCopy as DuplicateIcon,
  FolderOpen as FolderIcon,
  Archive as ArchiveIcon,
  TableChart as TableIcon,
  GridView as CardIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Business as BusinessIcon,
  Layers as LayersIcon,
  WorkOutline as ProjectIcon,
  AttachFile as AttachIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import * as actions from '../../redux/actions';
import StatusBadge from '../../components/Documents/StatusBadge';
import DocumentTypeBadge from '../../components/Documents/DocumentTypeBadge';

const STATUS_OPTIONS = [
  { value: '', label: 'ทุกสถานะ' },
  { value: 'draft', label: 'ฉบับร่าง' },
  { value: 'sent', label: 'ส่งแล้ว' },
  { value: 'approved', label: 'อนุมัติ' },
  { value: 'rejected', label: 'ปฏิเสธ' },
  { value: 'cancelled', label: 'ยกเลิก' },
];

const STATUS_TH = {
  draft: 'ฉบับร่าง',
  sent: 'ส่งแล้ว',
  approved: 'อนุมัติแล้ว',
  rejected: 'ถูกปฏิเสธ',
  cancelled: 'ยกเลิกแล้ว',
};

const STATUS_CHIP = {
  draft:     { label: 'ร่าง',     color: '#6b7280', bg: '#f3f4f6' },
  sent:      { label: 'ส่งแล้ว',  color: '#2563eb', bg: '#eff6ff' },
  approved:  { label: 'อนุมัติ',  color: '#16a34a', bg: '#f0fdf4' },
  rejected:  { label: 'ปฏิเสธ',  color: '#dc2626', bg: '#fef2f2' },
  cancelled: { label: 'ยกเลิก',  color: '#9ca3af', bg: '#f9fafb' },
};

function relativeDate(dateStr) {
  if (!dateStr) return '';
  const d = dayjs(dateStr);
  const diffDays = dayjs().diff(d, 'day');
  if (diffDays === 0) return 'วันนี้';
  if (diffDays === 1) return 'เมื่อวาน';
  if (diffDays < 7) return `${diffDays} วันที่แล้ว`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} สัปดาห์ที่แล้ว`;
  return d.format('DD/MM/YY');
}

const TYPE_TABS = [
  { value: '', label: 'ทั้งหมด' },
  { value: 'quotation', label: 'ใบเสนอราคา' },
  { value: 'delivery_note', label: 'ใบส่งงาน' },
  { value: 'billing_note', label: 'ใบวางบิล' },
  { value: 'receipt', label: 'ใบเสร็จรับเงิน' },
  { value: 'tax_invoice', label: 'ใบกำกับภาษี' },
];

const CHAIN_TYPES = [
  { type: 'quotation',     label: 'ใบเสนอราคา',      color: '#1565C0', bg: '#E3F2FD' },
  { type: 'delivery_note', label: 'ใบส่งงาน',         color: '#E65100', bg: '#FFF3E0' },
  { type: 'billing_note',  label: 'ใบวางบิล',         color: '#7B1FA2', bg: '#F3E5F5' },
  { type: 'receipt',       label: 'ใบเสร็จรับเงิน',   color: '#065F46', bg: '#D1FAE5' },
  { type: 'tax_invoice',   label: 'ใบกำกับภาษี',      color: '#1B5E20', bg: '#E8F5E9' },
];

function fmt(n) {
  return Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function chainBestTotal(chainDocs = []) {
  for (const t of ['tax_invoice', 'receipt', 'billing_note', 'delivery_note', 'quotation']) {
    const doc = chainDocs
      .filter((d) => d.document_type === t && d.status !== 'cancelled' && d.status !== 'rejected')
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    if (doc?.total) return doc.total;
  }
  return 0;
}

// ─── Single chain stepper row ───────────────────────────────────────────────
function ChainRow({ chain, onNavigate }) {
  const { docs, isArchived } = chain;
  const getLatest = (type) => docs
    .filter((d) => d.document_type === type)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  const latestDoc = [...docs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      gap={0.5}
      sx={{
        bgcolor: isArchived ? '#f0fdf4' : '#f8fafc',
        border: `1px solid ${isArchived ? '#bbf7d0' : '#eef2f7'}`,
        borderRadius: 2,
        px: 1.5,
        py: 0.875,
      }}
    >
      {CHAIN_TYPES.map((ct, idx) => {
        const doc = getLatest(ct.type);
        const hasPO = doc && ct.type === 'delivery_note' && doc.po_document_url;
        return (
          <React.Fragment key={ct.type}>
            <Stack direction="row" alignItems="center" gap={0.25}>
              {doc ? (
                <Box
                  onClick={() => onNavigate(doc._id)}
                  sx={{
                    display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                    bgcolor: ct.bg, border: `1.5px solid ${ct.color}40`,
                    borderRadius: 1.5, px: 1, py: 0.375, cursor: 'pointer',
                    opacity: doc.status === 'cancelled' || doc.status === 'rejected' ? 0.45 : 1,
                    transition: 'all 0.15s',
                    '&:hover': { filter: 'brightness(0.92)', transform: 'translateY(-1px)' },
                  }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: ct.color, lineHeight: 1.2 }}>
                    {ct.label}
                  </Typography>
                  <Typography sx={{ fontSize: 9.5, fontWeight: 600, color: ct.color, opacity: 0.75, fontFamily: 'monospace', lineHeight: 1.2, letterSpacing: 0.3 }}>
                    {doc.document_number}
                  </Typography>
                  {(() => {
                    const s = STATUS_CHIP[doc.status];
                    return s ? (
                      <Box sx={{ bgcolor: s.bg, border: `1px solid ${s.color}30`, borderRadius: 0.75, px: 0.5, mt: 0.25 }}>
                        <Typography sx={{ fontSize: 9, fontWeight: 700, color: s.color, lineHeight: 1.3 }}>
                          {s.label}
                        </Typography>
                      </Box>
                    ) : null;
                  })()}
                </Box>
              ) : (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center',
                  borderRadius: 1.5, px: 1, py: 0.625,
                  border: '1.5px dashed #e5e7eb',
                }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#d1d5db' }}>
                    {ct.label}
                  </Typography>
                </Box>
              )}
              {hasPO && (
                <Tooltip title="ดูใบ PO จากลูกค้า" arrow>
                  <Box
                    onClick={(e) => { e.stopPropagation(); window.open(doc.po_document_url, '_blank'); }}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 18, height: 18, borderRadius: 1, cursor: 'pointer',
                      bgcolor: '#dcfce7', border: '1px solid #86efac',
                      '&:hover': { bgcolor: '#bbf7d0' },
                    }}
                  >
                    <AttachIcon sx={{ fontSize: 11, color: '#15803d' }} />
                  </Box>
                </Tooltip>
              )}
            </Stack>
            {idx < CHAIN_TYPES.length - 1 && (
              <Typography sx={{ color: '#d1d5db', fontSize: 13, lineHeight: 1, mx: 0.25 }}>›</Typography>
            )}
          </React.Fragment>
        );
      })}

      <Stack direction="row" alignItems="center" gap={1} sx={{ ml: 'auto !important' }}>
        {isArchived && (
          <Chip
            label="เสร็จสิ้น"
            size="small"
            sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: 10, height: 18, border: '1px solid #bbf7d0' }}
          />
        )}
        {latestDoc && (
          <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, fontFamily: 'monospace' }}>
            {latestDoc.document_number}
          </Typography>
        )}
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>
          ฿{fmt(chainBestTotal(docs))}
        </Typography>
      </Stack>
    </Stack>
  );
}

// ─── Project card (header + chain rows) ─────────────────────────────────────
function ProjectCard({ project, onNavigate }) {
  const theme = useTheme();
  const projectTotal = project.chains.reduce((sum, c) => sum + chainBestTotal(c.docs), 0);

  return (
    <Box
      sx={{
        borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        pl: 1.5,
        transition: 'border-color 0.15s',
        '&:hover': { borderLeft: `3px solid ${theme.palette.primary.main}` },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
        <Stack direction="row" alignItems="center" gap={0.75} sx={{ flex: 1, minWidth: 0, mr: 1 }}>
          <ProjectIcon sx={{ fontSize: 13, color: theme.palette.primary.light, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: theme.palette.primary.main, textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>
            โปรเจกต์
          </Typography>
          <Typography
            variant="body2"
            fontWeight={700}
            noWrap
            sx={{ fontSize: 13, color: '#1e293b' }}
          >
            {project.project_name || 'ไม่ระบุโปรเจกต์'}
          </Typography>
          {project.lastActivity && (
            <Typography sx={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>
              · {relativeDate(project.lastActivity)}
            </Typography>
          )}
        </Stack>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: theme.palette.primary.main, whiteSpace: 'nowrap' }}>
          ฿{fmt(projectTotal)}
        </Typography>
      </Stack>

      <Stack spacing={0.625}>
        {project.chains.map((chain, idx) => (
          <ChainRow key={idx} chain={chain} onNavigate={onNavigate} />
        ))}
      </Stack>
    </Box>
  );
}

// ─── Customer section — own Paper per customer ───────────────────────────────
function CustomerSection({ customerName, projects, onNavigate }) {
  const theme = useTheme();
  const totalValue = projects.reduce(
    (sum, p) => sum + p.chains.reduce((s, c) => s + chainBestTotal(c.docs), 0),
    0,
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: `0 4px 24px ${alpha(theme.palette.primary.main, 0.1)}` },
      }}
    >
      {/* Customer header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.25,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Box sx={{
            px: 0.75, py: 0.2,
            bgcolor: 'rgba(255,255,255,0.15)',
            borderRadius: 1,
            border: '1px solid rgba(255,255,255,0.25)',
          }}>
            <Typography sx={{ fontSize: 9.5, color: 'rgba(255,255,255,0.85)', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
              ลูกค้า
            </Typography>
          </Box>
          <BusinessIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.7)' }} />
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#fff', letterSpacing: 0.1 }}>
            {customerName}
          </Typography>
          <Box sx={{ px: 0.875, py: 0.2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 10, ml: 0.25 }}>
            <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
              {projects.length} โปรเจกต์
            </Typography>
          </Box>
        </Stack>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'rgba(255,255,255,0.95)' }}>
          ฿{fmt(totalValue)}
        </Typography>
      </Stack>

      {/* Project list */}
      <Stack spacing={0} divider={<Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.08) }} />} sx={{ bgcolor: '#fff' }}>
        {projects.map((project, idx) => (
          <Box key={project.project_id?._id || idx} sx={{ px: 2, py: 1.5 }}>
            <ProjectCard project={project} onNavigate={onNavigate} />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

export default function DocumentList({ title }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { rows = [], total = 0, isLoading } = useSelector((s) => s.document);

  const [viewMode, setViewMode] = useState('card');
  const [jobTab, setJobTab] = useState('active');
  const [createAnchor, setCreateAnchor] = useState(null);
  const [tabType, setTabType] = useState('');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);

  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const data = await dispatch(actions.documentGroupedByProject({
        q,
        archived: jobTab === 'archived' ? 'true' : jobTab === 'active' ? 'false' : 'all',
      }));
      setGroups(Array.isArray(data) ? data : []);
    } catch {
      setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  }, [dispatch, q, jobTab]);

  const fetchDocs = useCallback(() => {
    dispatch(actions.documentAll({
      type: tabType || undefined,
      status: status || undefined,
      q: q || undefined,
      page: page + 1,
      limit: rowsPerPage,
    }));
  }, [dispatch, tabType, status, q, page, rowsPerPage]);

  useEffect(() => {
    if (viewMode === 'card') fetchGroups();
    else fetchDocs();
  }, [viewMode, fetchGroups, fetchDocs]);

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Group projects by customer, sort projects and customers by lastActivity desc
  const customerGroups = useMemo(() => {
    const map = {};
    groups.forEach((project) => {
      const key = project.customer_name || '—';
      if (!map[key]) map[key] = [];
      map[key].push(project);
    });
    return Object.entries(map)
      .map(([name, projects]) => {
        const sorted = [...projects].sort(
          (a, b) => new Date(b.lastActivity || 0) - new Date(a.lastActivity || 0),
        );
        const latestActivity = sorted[0]?.lastActivity || 0;
        return [name, sorted, latestActivity];
      })
      .sort((a, b) => new Date(b[2]) - new Date(a[2]))
      .map(([name, projects]) => [name, projects]);
  }, [groups]);

  const handleDuplicate = async (e, id) => {
    e.stopPropagation();
    await dispatch(actions.documentDuplicate(id));
    fetchDocs();
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{title || 'เอกสารงาน'}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={viewMode === 'card' ? 'ดูแบบตาราง' : 'ดูแบบ Job'}>
            <IconButton
              size="small"
              onClick={() => setViewMode((v) => (v === 'card' ? 'table' : 'card'))}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
            >
              {viewMode === 'card' ? <TableIcon fontSize="small" /> : <CardIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            endIcon={<ArrowDownIcon />}
            onClick={(e) => setCreateAnchor(e.currentTarget)}
            sx={{ borderRadius: 2 }}
          >
            สร้างเอกสาร
          </Button>
          <Menu
            anchorEl={createAnchor}
            open={Boolean(createAnchor)}
            onClose={() => setCreateAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { setCreateAnchor(null); history.push('/sale/documents/new?type=quotation'); }}>
              ใบเสนอราคา (QT)
            </MenuItem>
            <MenuItem onClick={() => { setCreateAnchor(null); history.push('/sale/documents/new?type=delivery_note'); }}>
              ใบส่งงาน (DN)
            </MenuItem>
            <MenuItem onClick={() => { setCreateAnchor(null); history.push('/sale/documents/new?type=billing_note'); }}>
              ใบวางบิล (BL)
            </MenuItem>
            <MenuItem onClick={() => { setCreateAnchor(null); history.push('/sale/documents/new?type=receipt'); }}>
              ใบเสร็จรับเงิน (REC)
            </MenuItem>
            <MenuItem onClick={() => { setCreateAnchor(null); history.push('/sale/documents/new?type=tax_invoice'); }}>
              ใบแจ้งหนี้/ใบกำกับภาษี (INV)
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {/* ─── Card (Job) View ─── */}
      {viewMode === 'card' && (
        <>
          <Paper variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
            <Tabs
              value={jobTab}
              onChange={(_, v) => setJobTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}
            >
              <Tab
                value="active"
                label="กำลังดำเนินการ"
                icon={<FolderIcon sx={{ fontSize: 15 }} />}
                iconPosition="start"
                sx={{ fontWeight: 600 }}
              />
              <Tab
                value="archived"
                label="Archive (INV อนุมัติแล้ว)"
                icon={<ArchiveIcon sx={{ fontSize: 15 }} />}
                iconPosition="start"
                sx={{ fontWeight: 600 }}
              />
              <Tab
                value="all"
                label="ทั้งหมด"
                icon={<LayersIcon sx={{ fontSize: 15 }} />}
                iconPosition="start"
                sx={{ fontWeight: 600 }}
              />
            </Tabs>
            <Box sx={{ p: 2 }}>
              <TextField
                size="small"
                placeholder="ค้นหาโปรเจกต์, ลูกค้า, เลขเอกสาร..."
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
                sx={{ minWidth: 300, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>
          </Paper>

          {groupsLoading ? (
            <Stack spacing={2}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
              ))}
            </Stack>
          ) : customerGroups.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <Typography>ไม่พบงาน</Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {customerGroups.map(([customerName, projects]) => (
                <CustomerSection
                  key={customerName}
                  customerName={customerName}
                  projects={projects}
                  onNavigate={(docId) => history.push(`/sale/documents/${docId}`)}
                />
              ))}
            </Stack>
          )}
        </>
      )}

      {/* ─── Table View ─── */}
      {viewMode === 'table' && (
        <Paper sx={{ borderRadius: 3 }} elevation={0} variant="outlined">
          <>
            <Tabs
              value={tabType}
              onChange={(_, v) => { setTabType(v); setPage(0); }}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}
            >
              {TYPE_TABS.map((t) => (
                <Tab key={t.value} value={t.value} label={t.label} sx={{ fontWeight: 600 }} />
              ))}
            </Tabs>

            <Stack direction="row" spacing={2} sx={{ p: 2 }} alignItems="center" flexWrap="wrap">
              <TextField
                size="small"
                placeholder="ค้นหาเลขที่เอกสาร, ลูกค้า, โปรเจกต์..."
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
                sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>สถานะ</InputLabel>
                <Select
                  value={status}
                  label="สถานะ"
                  onChange={(e) => { setStatus(e.target.value); setPage(0); }}
                  sx={{ borderRadius: 2 }}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700, width: 150 }}>เลขที่เอกสาร</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>ลูกค้า</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>โปรเจกต์</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 110 }}>วันที่</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 130, textAlign: 'right' }}>ยอดรวม</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 130 }}>ประเภท</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 110 }}>สถานะ</TableCell>
                    <TableCell sx={{ width: 80 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading
                    ? Array.from({ length: 6 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 8 }).map((__, j) => (
                            <TableCell key={j}><Skeleton variant="text" /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    : rows.length === 0
                    ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                            ไม่พบเอกสาร
                          </TableCell>
                        </TableRow>
                      )
                    : rows.map((doc) => (
                        <TableRow
                          key={doc._id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => history.push(`/sale/documents/${doc._id}`)}
                        >
                          <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {doc.document_number}
                          </TableCell>
                          <TableCell>{doc.snapshot?.customer_name || '-'}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Typography variant="body2" noWrap>
                              {doc.snapshot?.project_name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {doc.date ? dayjs(doc.date).format('DD/MM/YYYY') : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                            ฿{fmt(doc.total)}
                          </TableCell>
                          <TableCell><DocumentTypeBadge type={doc.document_type} /></TableCell>
                          <TableCell><StatusBadge status={doc.status} /></TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="ดูรายละเอียด">
                                <IconButton size="small" onClick={() => history.push(`/sale/documents/${doc._id}`)}>
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="ทำสำเนา">
                                <IconButton size="small" onClick={(e) => handleDuplicate(e, doc._id)}>
                                  <DuplicateIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                  }
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="แถวต่อหน้า"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} จาก ${count}`}
            />
          </>
        </Paper>
      )}
    </Box>
  );
}
